import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Send, FileText, ArrowLeft, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return "Today";
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function MessageThread() {
  const { hireId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [hire, setHire] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const loadHire = async () => {
    try {
      const { data } = await api.get(`/hires/${hireId}`);
      setHire(data);
    } catch { toast.error("Deal not found"); navigate("/messages"); }
  };
  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/hires/${hireId}/messages`);
      setMessages(data);
    } catch { /* empty */ }
  };
  useEffect(() => {
    if (loading || !user) return;
    loadHire(); loadMessages();
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [hireId, user, loading]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setText("");
    // Optimistic
    const optimistic = {
      id: `tmp-${Date.now()}`,
      hire_id: hireId,
      sender_id: user.id,
      sender_name: user.name,
      text: t,
      read_by: [user.id],
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    try {
      await api.post(`/hires/${hireId}/messages`, { text: t });
      await loadMessages();
    } catch (e) {
      toast.error("Failed to send");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setText(t);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!hire) return (<div className="min-h-screen grain"><Navbar /><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></div>);

  const other_name = user?.role === "brand" ? (hire.package?.title || "Creator") : (hire.package?.title || "Brand");
  const other_id = user?.role === "brand" ? hire.creator_id : hire.brand_id;

  // Group messages by day
  const groups = [];
  let lastDay = null;
  for (const m of messages) {
    const day = fmtDay(m.created_at);
    if (day !== lastDay) { groups.push({ day, items: [] }); lastDay = day; }
    groups[groups.length - 1].items.push(m);
  }

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/messages" label="All conversations" />
      </div>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-4 pb-24">
        <div className="glass rounded-3xl overflow-hidden flex flex-col" style={{ height: "70vh", minHeight: 460 }}>
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <Link to="/messages" className="lg:hidden text-zinc-400 hover:text-white" data-testid="msg-thread-back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="h-10 w-10 rounded-full nexus-gradient flex items-center justify-center text-white font-display font-bold">
              {(hire.package?.title || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{hire.package?.title || "Collab"}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Status · {hire.status.replace(/_/g, " ")}</div>
            </div>
            <Link
              to={`/deals/${hireId}`}
              className="text-[10px] uppercase tracking-widest text-nexus-blue hover:text-white inline-flex items-center gap-1"
              data-testid="msg-back-to-briefing"
            >
              <FileText className="h-3.5 w-3.5" /> Briefing
            </Link>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#060608]/50" data-testid="msg-list">
            {groups.length === 0 && (
              <div className="text-center text-zinc-500 text-sm py-8">Start the conversation.</div>
            )}
            {groups.map((g, gi) => (
              <div key={gi} className="space-y-2">
                <div className="text-center">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 bg-[#0C0C10] px-3 py-1 rounded-full border border-white/5">{g.day}</span>
                </div>
                {g.items.map((m) => {
                  const mine = m.sender_id === user.id;
                  const readByOther = (m.read_by || []).includes(other_id);
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "nexus-gradient text-white rounded-br-md" : "bg-white/[0.04] text-zinc-100 rounded-bl-md hairline"}`}>
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`flex items-center gap-1.5 mt-1 text-[10px] ${mine ? "text-white/70" : "text-zinc-500"}`}>
                          <span>{fmtTime(m.created_at)}</span>
                          {mine && (
                            m._pending ? <span>· sending…</span>
                            : readByOther ? <CheckCheck className="h-3 w-3 text-white" data-testid={`seen-${m.id}`} />
                            : <Check className="h-3 w-3" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-white/10 bg-[#0C0C10]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Message…"
                rows={1}
                autoCorrect="on"
                autoCapitalize="sentences"
                spellCheck
                className="flex-1 resize-none bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/25 max-h-32"
                data-testid="msg-input"
              />
              <button
                onClick={send}
                disabled={sending || !text.trim()}
                className="h-10 w-10 rounded-full nexus-gradient text-white flex items-center justify-center disabled:opacity-40"
                data-testid="msg-send-btn"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
